import { keepPreviousData } from '@tanstack/react-query';
import { type CSSProperties, Fragment, useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { SbIcon } from '../../design/sb-icon.js';
import { trpc } from '../../trpc-client.js';
import { useDebouncedTextFilter } from '../config-list-text-filter.js';
import './skills-list.css';

export type ConfigRepoSkillStatus =
  | 'new'
  | 'in_sync'
  | 'changed'
  | 'unpublished'
  | 'deleted'
  | 'error';

export interface ConfigRepoSkillRow {
  readonly skill_name: string;
  readonly description: string | null;
  readonly repo_tree_hash: string | null;
  readonly published_tree_hash: string | null;
  readonly ma_skill_id: string | null;
  readonly status: ConfigRepoSkillStatus;
  readonly repo_path: string | null;
  readonly scanned_at: string | null;
  readonly published_at: string | null;
  readonly updated_at: string | null;
  readonly scan_error: string | null;
}

type SkillSortBy = 'skill_name' | 'status' | 'updated_at' | 'scanned_at' | 'published_at';
type SortDirection = 'asc' | 'desc';

export interface SkillsListInput {
  readonly limit: number;
  readonly q?: string;
  readonly sort_by: SkillSortBy;
  readonly sort_dir: SortDirection;
  readonly cursor?: string;
  readonly status?: ConfigRepoSkillStatus;
  readonly skill_ma_id?: string;
}

export interface SkillActionButtonProps {
  readonly status: ConfigRepoSkillStatus;
  readonly isPending: boolean;
  readonly onSync: () => void;
  readonly onDelete: () => void;
}

export type SkillAction =
  | { readonly kind: 'sync'; readonly label: 'Sync' }
  | { readonly kind: 'delete'; readonly label: 'Delete' };

/** Sync-state chip presentation for the registry table (design 1f). */
export interface SkillSyncTag {
  readonly label: string;
  readonly className: string;
  readonly style?: CSSProperties;
}

const PAGE_SIZES = [10, 25, 50, 100] as const;
const SKILL_SORT_OPTIONS: ReadonlyArray<{ readonly value: SkillSortBy; readonly label: string }> = [
  { value: 'skill_name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'scanned_at', label: 'Scanned' },
  { value: 'published_at', label: 'Published' },
];
const SKILL_STATUS_OPTIONS: ReadonlyArray<{
  readonly value: ConfigRepoSkillStatus;
  readonly label: string;
}> = [
  { value: 'new', label: 'New' },
  { value: 'changed', label: 'Changed' },
  { value: 'unpublished', label: 'Unpublished' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'in_sync', label: 'In sync' },
  { value: 'error', label: 'Error' },
];

/**
 * The segmented sync-status filter (design 1f). Maps the design's four
 * buttons to the page's EXISTING status vocabulary — `null` = All (no
 * filter). The remaining statuses (`unpublished` / `deleted` / `error`)
 * have no dedicated segment; they stay reachable via All and, when a row
 * deep-links one, via the removable filter chip below.
 */
const STATUS_SEGMENTS: ReadonlyArray<{
  readonly label: string;
  readonly value: ConfigRepoSkillStatus | null;
}> = [
  { label: 'All', value: null },
  { label: 'In sync', value: 'in_sync' },
  { label: 'Modified', value: 'changed' },
  { label: 'New', value: 'new' },
];
const SEGMENTED_STATUS_VALUES = new Set<ConfigRepoSkillStatus>(['in_sync', 'changed', 'new']);

const DANGER_TAG_STYLE: CSSProperties = {
  background: 'var(--color-background-danger-subtle)',
  color: 'var(--color-text-danger)',
};

export function formatSkillStatus(status: ConfigRepoSkillStatus): string {
  switch (status) {
    case 'new':
      return 'New';
    case 'in_sync':
      return 'In sync';
    case 'changed':
      return 'Changed';
    case 'unpublished':
      return 'Unpublished';
    case 'deleted':
      return 'Deleted';
    case 'error':
      return 'Error';
  }
}

/**
 * Sync-state → design-system `.tag` mapping for the registry table.
 * Distinct from `formatSkillStatus` (used by the inspect disclosure): the
 * table adopts the design's label vocabulary ("Modified" for `changed`)
 * and colour tones, while the mutation kind still flows from
 * `getSkillAction`.
 */
export function syncTag(status: ConfigRepoSkillStatus): SkillSyncTag {
  switch (status) {
    case 'in_sync':
      return { label: 'In sync', className: 'tag tag--green' };
    case 'changed':
      return { label: 'Modified', className: 'tag tag--yellow' };
    case 'new':
      return { label: 'New', className: 'tag tag--cyan' };
    case 'unpublished':
      return { label: 'Unpublished', className: 'tag tag--grey' };
    case 'deleted':
      return {
        label: 'Deleted',
        className: 'tag skills-registry__tag-danger',
        style: DANGER_TAG_STYLE,
      };
    case 'error':
      return {
        label: 'Error',
        className: 'tag skills-registry__tag-danger',
        style: DANGER_TAG_STYLE,
      };
  }
}

export function canSyncSkill(status: ConfigRepoSkillStatus): boolean {
  return status === 'new' || status === 'changed' || status === 'unpublished';
}

export function getSkillAction(status: ConfigRepoSkillStatus): SkillAction | null {
  if (canSyncSkill(status)) {
    return { kind: 'sync', label: 'Sync' };
  }
  if (status === 'deleted') {
    return { kind: 'delete', label: 'Delete' };
  }
  return null;
}

export function SkillActionButton(props: SkillActionButtonProps): React.ReactElement | null {
  const action = getSkillAction(props.status);
  if (action === null) return null;
  const isDelete = action.kind === 'delete';
  // A brand-new skill's first sync is a "Publish" in the design's language;
  // an existing-but-drifted skill is a "Sync". Both drive `skills.sync`.
  const isPublish = action.kind === 'sync' && props.status === 'new';
  const label = isDelete ? 'Delete' : isPublish ? 'Publish' : 'Sync';
  const pendingLabel = isDelete ? 'Deleting…' : isPublish ? 'Publishing…' : 'Syncing…';
  const className = isDelete
    ? 'btn btn--secondary btn--xs skills-registry__danger'
    : 'btn btn--secondary btn--xs';
  return (
    <button
      type="button"
      className={className}
      disabled={props.isPending}
      onClick={isDelete ? props.onDelete : props.onSync}
    >
      {props.isPending ? pendingLabel : label}
    </button>
  );
}

export function readSkillsListInput(params: URLSearchParams): SkillsListInput {
  const rawLimit = Number(params.get('limit') ?? '10');
  const limit = PAGE_SIZES.includes(rawLimit as (typeof PAGE_SIZES)[number]) ? rawLimit : 10;
  const sortBy = params.get('sort_by');
  const sort_by = SKILL_SORT_OPTIONS.some((option) => option.value === sortBy)
    ? (sortBy as SkillSortBy)
    : 'skill_name';
  const sortDir = params.get('sort_dir');
  const sort_dir: SortDirection = sortDir === 'desc' ? 'desc' : 'asc';
  const q = normalizedParam(params, 'q');
  const cursor = normalizedParam(params, 'cursor');
  const status = params.get('status');
  const skill_ma_id = normalizedParam(params, 'skill_ma_id');
  return {
    limit,
    sort_by,
    sort_dir,
    ...(q !== undefined ? { q } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(isSkillStatus(status) ? { status } : {}),
    ...(skill_ma_id !== undefined ? { skill_ma_id } : {}),
  };
}

function normalizedParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value.length === 0 ? undefined : value;
}

function isSkillStatus(value: string | null): value is ConfigRepoSkillStatus {
  return SKILL_STATUS_OPTIONS.some((option) => option.value === value);
}

function hasActiveFilters(input: SkillsListInput): boolean {
  return (
    input.q !== undefined ||
    input.status !== undefined ||
    input.skill_ma_id !== undefined ||
    input.cursor !== undefined
  );
}

function formatDate(value: string | null): string {
  if (value === null) return '—';
  return new Date(value).toLocaleString();
}

function shortHash(value: string | null): string {
  if (value === null || value.length === 0) return '—';
  return value.length <= 12 ? value : value.slice(0, 12);
}

/**
 * Per-skill config/status inspection disclosure (fn-52.4 / R4). Mirrors the
 * agent details disclosure (`agent-detail-section`, fn-52.3): a collapsed
 * `<details>` revealing the FULL config-repo skill projection (status,
 * ma_skill_id, repo_path, tree hashes, all timestamps, scan_error) read
 * from the existing list payload — there is no skills `byId` endpoint.
 *
 * Status renders via this page's OWN `formatSkillStatus` + the existing
 * `skills-status-<status>` chip class — NOT `StatusPill` (which understands
 * only the 8 session statuses and would collapse a `ConfigRepoSkillStatus`
 * to "unknown"), and NOT the agent-card dependency chip classes.
 *
 * `open` lets the registry table render it expanded inside a detail row
 * (where the row's own Inspect button owns the toggle and the summary is
 * CSS-hidden); standalone it stays a normal collapsed disclosure.
 */
export function SkillInspectSection({
  skill,
  open,
}: {
  skill: ConfigRepoSkillRow;
  open?: boolean;
}): React.ReactElement {
  return (
    <details className="skill-card-inspect" open={open}>
      <summary className="skill-card-inspect-summary">Inspect config</summary>
      <dl className="skill-card-inspect-meta">
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`sb-chip skills-status-${skill.status}`}>
              {formatSkillStatus(skill.status)}
            </span>
          </dd>
        </div>
        <div>
          <dt>MA skill id</dt>
          <dd>
            {skill.ma_skill_id !== null && skill.ma_skill_id.length > 0 ? (
              <code>{skill.ma_skill_id}</code>
            ) : (
              'Not synced'
            )}
          </dd>
        </div>
        <div>
          <dt>Repo path</dt>
          <dd>
            {skill.repo_path !== null && skill.repo_path.length > 0 ? (
              <code>{skill.repo_path}</code>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div>
          <dt>Repo tree hash</dt>
          <dd>
            <code>{shortHash(skill.repo_tree_hash)}</code>
          </dd>
        </div>
        <div>
          <dt>Published tree hash</dt>
          <dd>
            <code>{shortHash(skill.published_tree_hash)}</code>
          </dd>
        </div>
        <div>
          <dt>Scanned</dt>
          <dd>{formatDate(skill.scanned_at)}</dd>
        </div>
        <div>
          <dt>Published</dt>
          <dd>{formatDate(skill.published_at)}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{formatDate(skill.updated_at)}</dd>
        </div>
      </dl>
      {skill.scan_error !== null && skill.scan_error.length > 0 && (
        <p className="skills-row-error" role="alert">
          {skill.scan_error}
        </p>
      )}
    </details>
  );
}

const TABLE_COLUMN_COUNT = 6;

export function SkillsListPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  const listInput = readSkillsListInput(searchParams);
  const skillsQuery = trpc.skills.list.useQuery(listInput, { placeholderData: keepPreviousData });
  const scanConfigRepo = trpc.skills.scanConfigRepo.useMutation();
  const syncSkill = trpc.skills.sync.useMutation();
  const deleteSkill = trpc.skills.delete.useMutation();

  const skills = (skillsQuery.data?.data ?? []) as readonly ConfigRepoSkillRow[];
  const lastScanAt = skillsQuery.data?.last_scan_at ?? null;
  const nextCursor = skillsQuery.data?.next_cursor ?? null;

  const setListParam = useCallback(
    (updates: Readonly<Record<string, string | null>>, replace = true): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value.trim().length === 0) next.delete(key);
        else next.set(key, value);
      }
      if (!Object.prototype.hasOwnProperty.call(updates, 'cursor')) {
        next.delete('cursor');
        setPreviousCursors([]);
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const commitSearchFilter = useCallback(
    (value: string): void => setListParam({ q: value }),
    [setListParam],
  );
  const commitSkillMaIdFilter = useCallback(
    (value: string): void => setListParam({ skill_ma_id: value }),
    [setListParam],
  );
  const [searchDraft, setSearchDraft] = useDebouncedTextFilter(listInput.q, commitSearchFilter);
  const [skillMaIdDraft, setSkillMaIdDraft] = useDebouncedTextFilter(
    listInput.skill_ma_id,
    commitSkillMaIdFilter,
  );

  const clearCursorForScan = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('cursor');
    setPreviousCursors([]);
    setSearchParams(next, { replace: true });
  };

  const toggleInspect = (skillName: string): void =>
    setExpandedSkill((current) => (current === skillName ? null : skillName));

  const countLabel = `${skills.length} ${skills.length === 1 ? 'skill' : 'skills'}`;
  // A deep-linked status that the segmented control has no button for.
  const offSegmentStatus =
    listInput.status !== undefined && !SEGMENTED_STATUS_VALUES.has(listInput.status)
      ? listInput.status
      : null;

  return (
    <section className="skills-registry">
      <div className="page__header">
        <div>
          <div className="page__title">Skills</div>
          <div className="caption" style={{ marginTop: 2 }}>
            Last scanned {formatDate(lastScanAt)}
          </div>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="btn btn--tertiary"
            disabled={scanConfigRepo.isPending}
            onClick={() => {
              void scanConfigRepo
                .mutateAsync(undefined)
                .then(async () => {
                  clearCursorForScan();
                  await utils.skills.list.invalidate();
                })
                .catch(() => {});
            }}
          >
            <SbIcon name="clock-rotate-left" size={16} />
            {scanConfigRepo.isPending ? 'Scanning…' : 'Scan config repo'}
          </button>
        </div>
      </div>

      {skillsQuery.isPending ? (
        <p className="skills-registry__loading" aria-busy="true">
          Loading skills…
        </p>
      ) : skillsQuery.error !== null ? (
        <p className="skills-registry__error" role="alert">
          Failed to load skills: {skillsQuery.error.message}
        </p>
      ) : (
        <>
          <div className="filters">
            <div className="search-input">
              <SbIcon name="search" size={16} />
              <input
                type="search"
                value={searchDraft}
                aria-label="Search skills"
                placeholder="Search skills"
                onChange={(event) => setSearchDraft(event.target.value)}
              />
            </div>
            {/* biome-ignore lint/a11y/useSemanticElements: a segmented pressed-button filter is an ARIA button group, not a fieldset of form controls */}
            <div className="seg" role="group" aria-label="Filter skills by sync status">
              {STATUS_SEGMENTS.map((segment) => {
                const pressed =
                  segment.value === null
                    ? listInput.status === undefined
                    : listInput.status === segment.value;
                return (
                  <button
                    key={segment.label}
                    type="button"
                    className="seg__item"
                    aria-pressed={pressed}
                    onClick={() => setListParam({ status: segment.value })}
                  >
                    {segment.label}
                  </button>
                );
              })}
            </div>
            <span className="skills-registry__count">{countLabel}</span>
          </div>

          {offSegmentStatus !== null && (
            <div className="skills-registry__chips" aria-label="Active skills filters">
              <button
                type="button"
                className="filter-chip"
                onClick={() => setListParam({ status: null })}
              >
                Status: {formatSkillStatus(offSegmentStatus)}
                <SbIcon name="xmark" size={12} />
              </button>
            </div>
          )}

          <div className="skills-registry__toolbar" aria-label="Skills list options">
            <label className="skills-registry__field">
              <span className="skills-registry__field-label">MA skill ID</span>
              <input
                type="search"
                className="skills-registry__input"
                value={skillMaIdDraft}
                aria-label="Filter skills by Managed Agent skill ID"
                placeholder="Any"
                onChange={(event) => setSkillMaIdDraft(event.target.value)}
              />
            </label>
            <label className="skills-registry__field">
              <span className="skills-registry__field-label">Sort</span>
              <select
                className="skills-registry__select"
                value={listInput.sort_by}
                aria-label="Sort skills by"
                onChange={(event) => setListParam({ sort_by: event.target.value })}
              >
                {SKILL_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="skills-registry__field">
              <span className="skills-registry__field-label">Direction</span>
              <select
                className="skills-registry__select"
                value={listInput.sort_dir}
                aria-label="Sort skills direction"
                onChange={(event) => setListParam({ sort_dir: event.target.value })}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </label>
            <label className="skills-registry__field">
              <span className="skills-registry__field-label">Page size</span>
              <select
                className="skills-registry__select"
                value={String(listInput.limit)}
                aria-label="Skills page size"
                onChange={(event) => setListParam({ limit: event.target.value })}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {skills.length === 0 ? (
            <p className="skills-registry__empty">
              {hasActiveFilters(listInput)
                ? 'No skills match current filters.'
                : 'No skills scanned yet.'}
            </p>
          ) : (
            <div className="skills-registry__table-wrap">
              <table className="table" aria-label="Skills">
                <thead>
                  <tr>
                    <th style={{ width: '38%' }}>Skill</th>
                    <th>Sync</th>
                    <th>Repo</th>
                    <th>Published</th>
                    <th>Updated</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {skills.map((skill) => {
                    const tag = syncTag(skill.status);
                    const isExpanded = expandedSkill === skill.skill_name;
                    return (
                      <Fragment key={skill.skill_name}>
                        <tr>
                          <td>
                            <div className="matter">
                              <span className="name" style={{ color: 'var(--fg-brand)' }}>
                                {skill.skill_name}
                              </span>
                              <span className="caption">
                                {skill.description ?? 'No description'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={tag.className} style={tag.style}>
                              {tag.label}
                            </span>
                          </td>
                          <td>
                            <span className="skills-registry__mono">
                              {shortHash(skill.repo_tree_hash)}
                            </span>
                          </td>
                          <td>
                            {skill.published_tree_hash !== null &&
                            skill.published_tree_hash.length > 0 ? (
                              <span className="skills-registry__mono">
                                {shortHash(skill.published_tree_hash)}
                              </span>
                            ) : (
                              <span className="skills-registry__muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className="skills-registry__muted">
                              {formatDate(skill.updated_at)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="skills-registry__row-actions">
                              <button
                                type="button"
                                className="btn btn--ghost btn--xs skills-registry__inspect-toggle"
                                aria-expanded={isExpanded}
                                onClick={() => toggleInspect(skill.skill_name)}
                              >
                                Inspect
                                <SbIcon
                                  name="chevron-right"
                                  size={14}
                                  className="skills-registry__chevron"
                                />
                              </button>
                              <SkillActionButton
                                status={skill.status}
                                isPending={
                                  (syncSkill.isPending &&
                                    syncSkill.variables?.skillName === skill.skill_name) ||
                                  (deleteSkill.isPending &&
                                    deleteSkill.variables?.skillName === skill.skill_name)
                                }
                                onSync={() => {
                                  // Rejection surfaces via syncSkill.error (alert
                                  // below) — swallow here like scanConfigRepo.
                                  void syncSkill
                                    .mutateAsync({ skillName: skill.skill_name })
                                    .then(async () => {
                                      await utils.skills.list.invalidate();
                                    })
                                    .catch(() => {});
                                }}
                                onDelete={() => {
                                  void deleteSkill
                                    .mutateAsync({ skillName: skill.skill_name })
                                    .then(async () => {
                                      await utils.skills.list.invalidate();
                                    })
                                    .catch(() => {});
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="skills-registry__inspect-row">
                            <td
                              className="skills-registry__inspect-cell"
                              colSpan={TABLE_COLUMN_COUNT}
                            >
                              <SkillInspectSection skill={skill} open />
                              <Link
                                className="skills-registry__agents-link"
                                to={`/agents?section=config-repo&skill_name=${encodeURIComponent(skill.skill_name)}`}
                              >
                                <SbIcon name="sparkles" size={14} />
                                View agents using this skill
                                <SbIcon name="chevron-right" size={14} />
                              </Link>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="skills-registry__pagination" aria-label="Skills pagination">
            <button
              type="button"
              className="btn btn--tertiary btn--xs"
              disabled={previousCursors.length === 0}
              onClick={() => {
                const previous = previousCursors[previousCursors.length - 1] ?? '';
                setPreviousCursors((stack) => stack.slice(0, -1));
                setListParam({ cursor: previous.length > 0 ? previous : null }, false);
              }}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn--tertiary btn--xs"
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor === null) return;
                setPreviousCursors((stack) => [...stack, listInput.cursor ?? '']);
                setListParam({ cursor: nextCursor }, false);
              }}
            >
              Next
              <SbIcon name="chevron-right" size={14} />
            </button>
          </div>
        </>
      )}

      {scanConfigRepo.data !== undefined && (
        <p className="skills-registry__result">
          Scan found {scanConfigRepo.data.discovered_count} skills;{' '}
          {scanConfigRepo.data.valid_count} valid, {scanConfigRepo.data.invalid_count} invalid.
        </p>
      )}
      {scanConfigRepo.error !== null && (
        <p role="alert" className="skills-registry__error">
          Failed to scan config repo: {scanConfigRepo.error.message}
        </p>
      )}
      {syncSkill.error !== null && (
        <p role="alert" className="skills-registry__error">
          Failed to sync skill: {syncSkill.error.message}
        </p>
      )}
      {deleteSkill.error !== null && (
        <p role="alert" className="skills-registry__error">
          Failed to delete skill: {deleteSkill.error.message}
        </p>
      )}
    </section>
  );
}
